import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, type CurrentUser, type RequestDetail } from '../api';
import { Alert, Button, Card } from '../components/ui';
import { SignatureDialog } from './SignatureDialog';

type Mode = 'approve' | 'return' | 'reject' | null;

/** What an approver can do with a request sitting in the shared pool. */
export function ApprovalActions({ request, user }: { request: RequestDetail; user: CurrentUser }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isOwn = request.requestedBy.id === user.id;
  const canSelfApprove = user.grants.includes('SELF_APPROVE');
  const blocked = isOwn && !canSelfApprove;
  const someoneElseReviewing = request.reviewedBy && request.reviewedBy.id !== user.id;

  // Advisory marker so two approvers don't unknowingly work the same request.
  useEffect(() => {
    void api.claimRequest(request.id).catch(() => undefined);
    return () => {
      void api.releaseRequest(request.id).catch(() => undefined);
    };
  }, [request.id]);

  const settled = () => {
    void queryClient.invalidateQueries({ queryKey: ['queue'] });
    void queryClient.invalidateQueries({ queryKey: ['request', request.id] });
    void queryClient.invalidateQueries({ queryKey: ['requests'] });
  };

  const approve = useMutation({
    mutationFn: (signature: string) => api.approveRequest(request.id, signature),
    onSuccess: () => {
      setMode(null);
      settled();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not approve it.'),
  });

  const decide = useMutation({
    mutationFn: ({ action, text }: { action: 'return' | 'reject'; text: string }) =>
      action === 'return' ? api.returnRequest(request.id, text) : api.rejectRequest(request.id, text),
    onSuccess: () => {
      setMode(null);
      setComment('');
      settled();
      navigate('/queue');
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not do that.'),
  });

  return (
    <Card className="mt-6 p-6">
      <h2 className="text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
        Your decision
      </h2>

      {someoneElseReviewing && (
        <div className="mt-4">
          <Alert tone="warning">
            {request.reviewedBy?.displayName} opened this a few minutes ago. You can still act on it (whoever
            approves first wins), but it is worth a word first.
          </Alert>
        </div>
      )}

      {blocked && (
        <div className="mt-4">
          <Alert tone="warning">
            You raised this request, so somebody else has to approve it. An administrator can grant you
            self-approval if that is genuinely needed.
          </Alert>
        </div>
      )}

      {isOwn && canSelfApprove && (
        <div className="mt-4">
          <Alert tone="info">
            You raised this request and hold a self-approval grant. Approving it will be recorded as self-approved,
            on the request and in the audit log.
          </Alert>
        </div>
      )}

      {error && (
        <div className="mt-4">
          <Alert>{error}</Alert>
        </div>
      )}

      {mode === 'return' || mode === 'reject' ? (
        <div className="mt-4 max-w-2xl">
          <label htmlFor="decision-comment" className="block text-sm font-medium text-slate-800 dark:text-slate-200">
            {mode === 'return' ? 'What needs changing?' : 'Why is this being rejected?'}
          </label>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {mode === 'return'
              ? 'The requester sees this, fixes it and signs again before resubmitting.'
              : 'This is recorded on the request permanently. A rejected request cannot be reopened.'}
          </p>
          <textarea
            id="decision-comment"
            rows={4}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            maxLength={2000}
            className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
          <div className="mt-3 flex flex-wrap gap-3">
            <Button
              variant={mode === 'reject' ? 'danger' : 'primary'}
              disabled={comment.trim().length < 3 || decide.isPending}
              onClick={() => decide.mutate({ action: mode, text: comment.trim() })}
            >
              {decide.isPending ? 'Working…' : mode === 'return' ? 'Return for changes' : 'Reject request'}
            </Button>
            <Button variant="secondary" onClick={() => { setMode(null); setError(null); }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap gap-3">
          <Button onClick={() => { setError(null); setMode('approve'); }} disabled={blocked}>
            Approve &amp; sign
          </Button>
          <Button variant="secondary" onClick={() => { setError(null); setMode('return'); }}>
            Return for changes
          </Button>
          <Button variant="secondary" onClick={() => { setError(null); setMode('reject'); }}>
            Reject
          </Button>
          <Button variant="ghost" onClick={() => navigate(`/requests/${request.id}/edit`)}>
            Amend before approving
          </Button>
        </div>
      )}

      <SignatureDialog
        open={mode === 'approve'}
        title="Approve and sign"
        attestation={`You are approving ${request.reference} for ${
          request.employee?.fullName ?? 'this employee'
        }. Your signature goes onto the signed record along with the time, your address and a hash of the request exactly as it stands now.`}
        confirmLabel="Approve request"
        signerName={user.displayName}
        busy={approve.isPending}
        error={error}
        onCancel={() => setMode(null)}
        onConfirm={(dataUri) => approve.mutate(dataUri)}
      />
    </Card>
  );
}
